// ===========================================
// Dev Projects Page - Main Project Overview
// ===========================================
// Provides three view modes for projects:
// 1. Kanban - Drag & drop board for SDLC progression
// 2. Grid - Visual card-based layout
// 3. Table - Compact sortable list view

import React, { useEffect, useState } from 'react';
import { useProjects } from '../context/ProjectContext';
import ProjectCard from '../components/ProjectCard';
import KanbanBoard from '../components/KanbanBoard';
import ProjectTable from '../components/ProjectTable';
import CostOverviewTile from '../components/CostOverviewTile';
import { 
  FolderKanban, 
  Clock, 
  Sparkles,
  Filter,
  LayoutGrid,
  List,
  Kanban,
  Lightbulb,
  Play,
  Pause,
  CheckCircle,
  Archive
} from 'lucide-react';
import './Dashboard.scss';

const Dashboard = () => {
  const { projects, loading, error, fetchProjects, pagination } = useProjects();
  const [activeFilter, setActiveFilter] = useState('all');
  // View modes: 'kanban', 'grid', 'table'
  const [viewMode, setViewMode] = useState('kanban');
  // Mobile status filter for kanban view
  const [mobileKanbanFilter, setMobileKanbanFilter] = useState('all');

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Filter projects (for grid and table views - kanban shows all)
  const filteredProjects = projects.filter(project => {
    if (activeFilter === 'all') return true;
    return project.status === activeFilter;
  });

  // Calculate stats for the dashboard cards
  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'active').length,
    // Recently focused projects in the last 7 days
    recentlyFocused: projects.filter(p => {
      const lastFocused = new Date(p.lastFocused);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return lastFocused > weekAgo;
    }).length
  };

  // Recently focused projects (top 3) - only shown in grid view
  const recentProjects = [...projects]
    .sort((a, b) => new Date(b.lastFocused) - new Date(a.lastFocused))
    .slice(0, 3);

  // Status filter options
  const filterOptions = [
    { value: 'all', label: 'All Projects' },
    { value: 'active', label: 'Active' },
    { value: 'idea', label: 'Ideas' },
    { value: 'paused', label: 'Paused' },
    { value: 'completed', label: 'Completed' },
    { value: 'archived', label: 'Archived' }
  ];

  // Mobile status tabs configuration (for mobile kanban filter)
  const mobileStatusTabs = [
    { value: 'all', label: 'All', icon: FolderKanban, count: projects.length },
    { value: 'idea', label: 'Ideas', icon: Lightbulb, count: projects.filter(p => p.status === 'idea').length },
    { value: 'active', label: 'Active', icon: Play, count: projects.filter(p => p.status === 'active').length },
    { value: 'paused', label: 'Paused', icon: Pause, count: projects.filter(p => p.status === 'paused').length },
    { value: 'completed', label: 'Done', icon: CheckCircle, count: projects.filter(p => p.status === 'completed').length },
    { value: 'archived', label: 'Archive', icon: Archive, count: projects.filter(p => p.status === 'archived').length }
  ];

  // Render the appropriate view based on viewMode
  const renderProjectsView = () => {
    // Loading State
    if (loading) {
      return (
        <div className="dashboard__loading">
          <div className="spinner"></div>
          <p>Loading projects...</p>
        </div>
      );
    }

    // Error State
    if (error) {
      return (
        <div className="dashboard__error">
          <p>{error}</p>
          <button onClick={() => fetchProjects()} className="btn-secondary">
            Try Again
          </button>
        </div>
      );
    }

    // Kanban View - shows all projects organized by status
    if (viewMode === 'kanban') {
      return <KanbanBoard projects={projects} />;
    }

    // Table View - compact list with sorting
    if (viewMode === 'table') {
      return filteredProjects.length > 0 ? (
        <ProjectTable projects={filteredProjects} />
      ) : (
        <div className="dashboard__empty">
          <FolderKanban size={48} />
          <h4>No projects found</h4>
          <p>
            {activeFilter === 'all' 
              ? "Create your first project to get started!"
              : `No ${activeFilter} projects yet.`}
          </p>
        </div>
      );
    }

    // Grid View - card-based layout (default)
    return (
      <div className="dashboard__projects-grid">
        {filteredProjects.length > 0 ? (
          filteredProjects.map(project => (
            <ProjectCard 
              key={project._id || project.id} 
              project={project}
            />
          ))
        ) : (
          <div className="dashboard__empty">
            <FolderKanban size={48} />
            <h4>No projects found</h4>
            <p>
              {activeFilter === 'all' 
                ? "Create your first project to get started!"
                : `No ${activeFilter} projects yet.`}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard">
      {/* Page Header */}
      <header className="dashboard__header">
        <div>
          <h1>Dev Projects</h1>
          <p>Welcome back! Here's an overview of your projects.</p>
        </div>
      </header>

      {/* Stats Cards - 4 tiles including new CostOverviewTile */}
      <div className="dashboard__stats">
        {/* Total Projects */}
        <div className="stat-card">
          <div className="stat-card__icon" style={{ background: 'rgba(0, 212, 255, 0.1)' }}>
            <FolderKanban size={22} style={{ color: 'var(--color-accent)' }} />
          </div>
          <div className="stat-card__content">
            <span className="stat-card__value">{stats.total}</span>
            <span className="stat-card__label">Total Projects</span>
          </div>
        </div>
        
        {/* Active Projects */}
        <div className="stat-card">
          <div className="stat-card__icon" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
            <Sparkles size={22} style={{ color: 'var(--color-success)' }} />
          </div>
          <div className="stat-card__content">
            <span className="stat-card__value">{stats.active}</span>
            <span className="stat-card__label">Active Projects</span>
          </div>
        </div>
        
        {/* Cost Overview Tile - replaces simple Monthly Cost stat */}
        {/* Shows project costs + global subscriptions (Cursor, ChatGPT, etc.) */}
        <CostOverviewTile projects={projects} />
        
        {/* Active This Week */}
        <div className="stat-card">
          <div className="stat-card__icon" style={{ background: 'rgba(168, 85, 247, 0.1)' }}>
            <Clock size={22} style={{ color: 'var(--color-accent-tertiary)' }} />
          </div>
          <div className="stat-card__content">
            <span className="stat-card__value">{stats.recentlyFocused}</span>
            <span className="stat-card__label">Active This Week</span>
          </div>
        </div>
      </div>

      {/* Main Content - Full width, no sidebar */}
      <div className="dashboard__grid dashboard__grid--full">
        {/* Projects Section */}
        <section className="dashboard__projects">
          {/* Recently Focused - only show in grid view */}
          {viewMode === 'grid' && recentProjects.length > 0 && (
            <div className="dashboard__section">
              <h3 className="dashboard__section-title">
                <Clock size={18} />
                Recently Focused
              </h3>
              <div className="dashboard__recent-grid">
                {recentProjects.map(project => (
                  <ProjectCard key={project._id || project.id} project={project} compact />
                ))}
              </div>
            </div>
          )}

          {/* All Projects */}
          <div className="dashboard__section">
            <div className="dashboard__section-header">
              <h3 className="dashboard__section-title">
                <FolderKanban size={18} />
                All Projects
              </h3>
              
              <div className="dashboard__controls">
                {/* Status Filter - hide in kanban mode (shows all statuses as columns) */}
                {viewMode !== 'kanban' && (
                  <div className="dashboard__filter">
                    <Filter size={16} />
                    <select 
                      value={activeFilter}
                      onChange={(e) => setActiveFilter(e.target.value)}
                    >
                      {filterOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* View Mode Toggle - 3 options now */}
                <div className="dashboard__view-toggle">
                  <button 
                    className={viewMode === 'kanban' ? 'active' : ''}
                    onClick={() => setViewMode('kanban')}
                    title="Kanban Board"
                  >
                    <Kanban size={16} />
                  </button>
                  <button 
                    className={viewMode === 'grid' ? 'active' : ''}
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button 
                    className={viewMode === 'table' ? 'active' : ''}
                    onClick={() => setViewMode('table')}
                    title="Table View"
                  >
                    <List size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Status Tabs - Only visible on mobile in kanban mode */}
            {viewMode === 'kanban' && (
              <div className="mobile-status-tabs">
                {mobileStatusTabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.value}
                      className={`mobile-status-tabs__tab mobile-status-tabs__tab--${tab.value} ${mobileKanbanFilter === tab.value ? 'active' : ''}`}
                      onClick={() => setMobileKanbanFilter(tab.value)}
                    >
                      <Icon size={14} />
                      {tab.label}
                      {tab.count > 0 && (
                        <span className="mobile-status-tabs__count">{tab.count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Render the selected view */}
            {renderProjectsView()}

            {/* Pagination Info */}
            {pagination.total > 0 && (
              <div className="dashboard__pagination">
                Showing {filteredProjects.length} of {pagination.total} projects
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;



